/**
 * TheoremsPanel — reference page for the four theorem candidates
 * T1–T4 of the Two-Categorical CSK manuscript.
 *
 * Each theorem comes with the mandatory naming triad
 * (security / governance / mathematical), a Mono-rendered statement,
 * an operational paragraph, and an adversarial calibration. These are
 * the four refusals that together close the confused-deputy class.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import theoremData from "../data/theorems.json" with { type: "json" };
import { PanelSurface, panelTokens } from "@csk/demo-ui";

interface Triad {
  readonly security: string;
  readonly governance: string;
  readonly mathematical: string;
}

interface Theorem {
  readonly id: string;
  readonly shortName: string;
  readonly color: string;
  readonly triad: Triad;
  readonly thesisName: string | null;
  readonly statement: string;
  readonly operational: string;
  readonly adversarialCalibration: string;
  readonly neutralizes: string;
  readonly fmCollision: string;
}

const THEOREMS: readonly Theorem[] = theoremData.theorems.map((t) => ({
  id: t.id,
  shortName: t.shortName,
  color: t.color,
  triad: t.triad,
  thesisName: t.thesisName,
  statement: t.statement,
  operational: t.operational,
  adversarialCalibration: t.adversarialCalibration,
  neutralizes: t.neutralizes,
  fmCollision: t.fmCollision,
}));

interface InterlockEntry {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly crossLink: string;
  readonly color: string;
}

const INTERLOCK: readonly InterlockEntry[] = [
  {
    id: "T1",
    title: "T1 forecloses replay",
    summary:
      "Capability replay collapses to grade 0_G; the neutral-grade position is uninhabited.",
    crossLink: 'see Bridge Constructor — preset "consumed token replay".',
    color: "#22d3ee",
  },
  {
    id: "T2",
    title: "T2 forecloses cross-grade presentation",
    summary:
      "Bridges typed at distinct grades have no shared inhabitant; an edit token cannot pose as a deploy token.",
    crossLink:
      'see Bridge Constructor — preset "CD-1 — receipt presented as grant".',
    color: "#ec4899",
  },
  {
    id: "T3",
    title: "T3 forecloses composition drift",
    summary:
      "Composition adds grades in G componentwise; the composite carries exactly that sum. Same-SDP opposite-phase composites land on the ICG-s envelope (Case B); distinct-SDP composites land on a cross-SDP grade vector admissible only at a seam declaring that exact composite (Case C).",
    crossLink: "see Composition — Case A · B · C · D verdicts.",
    color: "#a78bfa",
  },
  {
    id: "T4",
    title: "T4 forecloses audit-log forgery",
    summary:
      "sdpIndex(B2) ≡ sdpIndex(B4); the audit log cannot retroactively claim a different SDP than the authorizer.",
    crossLink: 'see SNF Walker — preset "cross-SDP forgery".',
    color: "#fbbf24",
  },
];

const TRIAD_LABELS: Readonly<Record<keyof Triad, string>> = {
  security: "Security",
  governance: "Governance",
  mathematical: "Mathematical",
};

const TRIAD_COLORS: Readonly<Record<keyof Triad, string>> = {
  security: panelTokens.accentRed,
  governance: panelTokens.accentGreen,
  mathematical: panelTokens.accentPurple,
};

export function TheoremsPanel(): React.JSX.Element {
  const [activeId, setActiveId] = useState<string>(THEOREMS[0].id);

  const active = useMemo(
    () => THEOREMS.find((t) => t.id === activeId) ?? THEOREMS[0],
    [activeId],
  );

  const chipItems = useMemo(
    () =>
      THEOREMS.map((t) => ({
        id: t.id,
        label: `${t.id} · ${t.shortName}`,
        color: t.color,
      })),
    [],
  );

  return (
    <PanelSurface.PanelShell
      title="Theorems T1–T4"
      subtitle="STATEMENTS · OPERATIONAL · ADVERSARIAL CALIBRATION"
      accentColor="#14b8a6"
    >
      <PanelSurface.Card glowColor="#14b8a644">
        <PanelSurface.SectionHeading>
          Four theorem candidates, one structural class
        </PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          T1 through T4 are the four refusal theorems of chapter 0. They are{" "}
          <em>candidates</em> — chapter 0's status PanelSurface.badge reads{" "}
          <strong>DRAFT</strong>; full Agda mechanization for the graded
          refinements is staged in Appendix D rather than shipped today. Despite
          the draft status, the type-level shape of each refusal is already
          settled and is exactly what this demo's algorithms enforce.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          Together they cover every confused-deputy vector: replay (
          <PanelSurface.InlineCode>T1</PanelSurface.InlineCode>), cross-grade presentation (
          <PanelSurface.InlineCode>T2</PanelSurface.InlineCode>), composition drift (
          <PanelSurface.InlineCode>T3</PanelSurface.InlineCode>), and audit-log forgery (
          <PanelSurface.InlineCode>T4</PanelSurface.InlineCode>).
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Pick a theorem</PanelSurface.SectionHeading>
        <div className="mt-2.5">
          <PanelSurface.ChipRow
            items={chipItems}
            activeId={active.id}
            onChange={setActiveId}
            ariaLabel="Theorem selector"
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card glowColor={`${active.color}44`}>
        <div className="flex flex-wrap items-baseline gap-3.5">
          <h2
            className="m-0 font-mono text-[26px] font-extrabold tracking-[-0.01em]"
            style={{
              color: active.color,
              textShadow: `0 0 22px ${active.color}66`,
            }}
          >
            {active.id}
          </h2>
          <span className="text-[17px] font-bold text-[var(--tcc-bright)]">
            {active.shortName}
          </span>
          {active.thesisName && (
            <span className="rounded-md border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.55)] px-2 py-0.5 font-mono text-[12px] text-[var(--tcc-muted)]">
              {active.thesisName}
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(Object.keys(TRIAD_LABELS) as readonly (keyof Triad)[]).map((key) =>
            PanelSurface.badge(
              `${TRIAD_LABELS[key]} · ${active.triad[key]}`,
              TRIAD_COLORS[key],
              `${TRIAD_LABELS[key]} name from the mandatory triad`,
            ),
          )}
        </div>

        <div className="mt-[18px]">
          <PanelSurface.SectionHeading>Statement</PanelSurface.SectionHeading>
          <PanelSurface.Mono style={{ marginTop: 10 }}>{active.statement}</PanelSurface.Mono>
        </div>

        <div className="mt-[18px]">
          <PanelSurface.SectionHeading>What this looks like operationally</PanelSurface.SectionHeading>
          <p className="mt-2.5 text-[13.5px] leading-[1.65]">
            {active.operational}
          </p>
        </div>

        <div className="mt-[18px]">
          <PanelSurface.SectionHeading>Adversarial calibration</PanelSurface.SectionHeading>
          <p className="mt-2.5 text-[13.5px] leading-[1.65]">
            {active.adversarialCalibration}
          </p>
        </div>

        <div className="mt-5 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3.5">
          <SmallStatCard
            label="Neutralizes"
            value={active.neutralizes}
            accent={panelTokens.accentRed}
          />
          <SmallStatCard
            label="FM-0x collision"
            value={active.fmCollision}
            accent={panelTokens.accentAmber}
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>How T1–T4 interlock</PanelSurface.SectionHeading>
        <ul className="mt-3 flex list-none flex-col gap-2.5 p-0">
          {INTERLOCK.map((entry) => (
            <li
              key={entry.id}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,220px)] items-center gap-[18px] rounded-xl border border-solid px-3.5 py-3"
              style={{
                borderColor: `${entry.color}44`,
                background: `linear-gradient(135deg, ${entry.color}10, transparent 80%)`,
              }}
            >
              <div>
                <div
                  className="mb-1 font-mono text-[13.5px] font-bold tracking-[0.02em]"
                  style={{ color: entry.color }}
                >
                  {entry.title}
                </div>
                <div className="text-[13px] leading-[1.55] text-[var(--tcc-text)]">
                  {entry.summary}
                </div>
              </div>
              <div className="text-right font-mono text-[11.5px] leading-[1.45] text-[var(--tcc-muted)]">
                {entry.crossLink}
              </div>
            </li>
          ))}
        </ul>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}

interface SmallStatCardProps {
  readonly label: string;
  readonly value: string;
  readonly accent: string;
}

function SmallStatCard({
  label,
  value,
  accent,
}: SmallStatCardProps): React.JSX.Element {
  return (
    <div
      className="rounded-xl border border-solid px-3.5 py-3"
      style={{
        borderColor: `${accent}44`,
        background: `linear-gradient(180deg, ${accent}10, rgba(5,8,16,0.55))`,
      }}
    >
      <div
        className="mb-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em]"
        style={{ color: accent }}
      >
        {label}
      </div>
      <div className="text-[13.5px] font-semibold leading-[1.5] text-[var(--tcc-bright)]">
        {value}
      </div>
    </div>
  );
}
